import React, { useState, useEffect } from 'react';
import { 
  subscribe, 
  getBehavioralData, 
  getUserJourneys, 
  getCohortAnalysis, 
  getUserSegments, 
  getABTests, 
  getPerformanceMetrics, 
  getConversionFunnels, 
  getEngagementSummary, 
  getRealTimeMetrics,
  trackEvent,
  refreshData,
  getEngagementLevel,
  calculateRetentionRate
} from '../../../services/bread/userEngagementService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';

const UserEngagement = () => {
  const [engagementData, setEngagementData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('7d');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [realTimeData, setRealTimeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribe(setEngagementData);
    setLoading(false);
    
    // Track page view
    trackEvent('page_view', { page: 'user_engagement', section: 'analytics' });
    
    // Log access
    addAuditLog({ user: 'feros.khan@krotrust.org', action: 'page_access', target: 'user_engagement_analytics', status: 'success' });
    
    // Setup real-time data updates
    const realTimeInterval = setInterval(() => {
      setRealTimeData(getRealTimeMetrics());
    }, 30000); // Update every 30 seconds

    // Initial real-time data
    setRealTimeData(getRealTimeMetrics());

    return () => {
      unsubscribe();
      clearInterval(realTimeInterval);
    };
  }, []);

  const handleRefreshData = () => {
    setLoading(true);
    refreshData(30);
    addAuditLog({ user: 'feros.khan@krotrust.org', action: 'data_refresh', target: 'user_engagement_data', status: 'success' });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    trackEvent('tab_change', { from: activeTab, to: tab, section: 'user_engagement' });
  };

  if (loading || !engagementData) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading engagement data...</span>
          </div>
          <p className="mt-2">Loading user engagement analytics...</p>
        </div>
      </div>
    );
  }

  const summary = getEngagementSummary();
  const behavioralData = getBehavioralData();
  const journeys = getUserJourneys();
  const cohorts = getCohortAnalysis();
  const segments = getUserSegments();
  const abTests = getABTests();
  const performance = getPerformanceMetrics();
  const funnels = getConversionFunnels();

  const OverviewTab = () => {
    const engagementLevel = getEngagementLevel(summary?.avgEngagementScore || 0);
    
    return (
      <div className="row">
        {/* Real-time Metrics */}
        <div className="col-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-chart-line me-2"></i>Real-time Engagement
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="h2 text-success">{realTimeData?.activeUsers || 0}</div>
                    <small className="text-muted">Active Users</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="h2 text-info">{realTimeData?.activeSessions || 0}</div>
                    <small className="text-muted">Active Sessions</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="h2 text-warning">{realTimeData?.pageViewsPerMinute || 0}</div>
                    <small className="text-muted">Page Views/Min</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <div className="h2 text-primary">{Math.round(realTimeData?.avgSessionDuration / 60) || 0}m</div>
                    <small className="text-muted">Avg Session</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics Summary */}
        <div className="col-12 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-tachometer-alt me-2"></i>Engagement Summary (Last 7 Days)
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-primary">{summary?.totalUsers?.toLocaleString() || '0'}</div>
                    <small className="text-muted">Total Users</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-info">{summary?.totalSessions?.toLocaleString() || '0'}</div>
                    <small className="text-muted">Sessions</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-success">{summary?.sessionsPerUser || '0'}</div>
                    <small className="text-muted">Sessions/User</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-warning">{Math.round(summary?.avgSessionDuration / 60) || '0'}m</div>
                    <small className="text-muted">Avg Duration</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3" style={{ color: engagementLevel.color }}>
                      {summary?.avgEngagementScore || '0'}
                    </div>
                    <small className="text-muted">Engagement Score</small>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="text-center p-3 border rounded">
                    <div className="h3 text-danger">{Math.round((summary?.avgBounceRate || 0) * 100)}%</div>
                    <small className="text-muted">Bounce Rate</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Segments */}
        <div className="col-md-6 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-users me-2"></i>User Segments
              </h5>
            </div>
            <div className="card-body">
              {segments.map((segment, index) => (
                <div key={index} className="d-flex justify-content-between align-items-center mb-3 p-2 border rounded">
                  <div>
                    <div className="fw-bold">{segment.name}</div>
                    <small className="text-muted">{segment.size.toLocaleString()} users</small>
                  </div>
                  <div className="text-end">
                    <span className="badge" style={{ backgroundColor: segment.color, color: 'white' }}>
                      {segment.engagement}
                    </span>
                    <div className="small mt-1">{segment.growth}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="col-md-6 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-stopwatch me-2"></i>Performance Metrics
              </h5>
            </div>
            <div className="card-body">
              {performance.map((metric, index) => (
                <div key={index} className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div className="fw-bold">{metric.metric}</div>
                    <small className="text-muted">Target: {metric.target}{metric.unit}</small>
                  </div>
                  <div className="text-end">
                    <div className="h6 mb-0">{metric.value}{metric.unit}</div>
                    <span className={`small ${
                      metric.trend === 'improving' ? 'text-success' : 
                      metric.trend === 'declining' ? 'text-danger' : 'text-warning'
                    }`}>
                      {metric.trend}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BehavioralTab = () => (
    <div className="row">
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-chart-area me-2"></i>Behavioral Analytics
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Users</th>
                    <th>Sessions</th>
                    <th>Page Views</th>
                    <th>Bounce Rate</th>
                    <th>Avg Duration</th>
                    <th>Engagement Score</th>
                  </tr>
                </thead>
                <tbody>
                  {behavioralData.slice(-14).map((day, index) => {
                    const engagementLevel = getEngagementLevel(day.engagementScore);
                    return (
                      <tr key={index}>
                        <td>{new Date(day.date).toLocaleDateString()}</td>
                        <td>{day.users.toLocaleString()}</td>
                        <td>{day.sessions.toLocaleString()}</td>
                        <td>{day.pageViews.toLocaleString()}</td>
                        <td>
                          <span className={`badge ${
                            day.bounceRate < 0.3 ? 'bg-success' : 
                            day.bounceRate < 0.5 ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {Math.round(day.bounceRate * 100)}%
                          </span>
                        </td>
                        <td>{Math.round(day.avgSessionDuration / 60)}m {Math.round(day.avgSessionDuration % 60)}s</td>
                        <td>
                          <span className="badge" style={{ backgroundColor: engagementLevel.color, color: 'white' }}>
                            {day.engagementScore}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const JourneysTab = () => (
    <div className="row">
      {journeys.map((journey, index) => (
        <div key={index} className="col-md-6 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header">
              <h6 className="mb-0">{journey.name}</h6>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-6">
                  <div className="text-center">
                    <div className="h4 text-primary">{journey.sessions.toLocaleString()}</div>
                    <small className="text-muted">Sessions</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-center">
                    <div className="h4 text-success">{Math.round(journey.conversionRate * 100)}%</div>
                    <small className="text-muted">Conversion</small>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <strong>Top Pages:</strong>
                {journey.topPages.map((page, idx) => (
                  <div key={idx} className="d-flex justify-content-between mt-1">
                    <span>{page.page}</span>
                    <span className="text-muted">{page.views} views</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const CohortTab = () => (
    <div className="row">
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-layer-group me-2"></i>Cohort Retention Analysis
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead className="table-light">
                  <tr>
                    <th>Cohort</th>
                    <th>Size</th>
                    <th>Week 0</th>
                    <th>Week 1</th>
                    <th>Week 2</th>
                    <th>Week 4</th>
                    <th>Week 8</th>
                    <th>Week 12</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.slice(-8).map((cohort, index) => (
                    <tr key={index}>
                      <td>{new Date(cohort.cohort).toLocaleDateString()}</td>
                      <td>{cohort.size}</td>
                      <td>
                        <span className="badge bg-success">100%</span>
                      </td>
                      <td>
                        <span className={`badge ${cohort.week1 > 0.6 ? 'bg-success' : cohort.week1 > 0.4 ? 'bg-warning' : 'bg-danger'}`}>
                          {calculateRetentionRate(cohort, 1)}%
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${cohort.week2 > 0.5 ? 'bg-success' : cohort.week2 > 0.3 ? 'bg-warning' : 'bg-danger'}`}>
                          {calculateRetentionRate(cohort, 2)}%
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${cohort.week4 > 0.4 ? 'bg-success' : cohort.week4 > 0.25 ? 'bg-warning' : 'bg-danger'}`}>
                          {calculateRetentionRate(cohort, 4)}%
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${cohort.week8 > 0.3 ? 'bg-success' : cohort.week8 > 0.15 ? 'bg-warning' : 'bg-danger'}`}>
                          {calculateRetentionRate(cohort, 8)}%
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${cohort.week12 > 0.2 ? 'bg-success' : cohort.week12 > 0.1 ? 'bg-warning' : 'bg-danger'}`}>
                          {calculateRetentionRate(cohort, 12)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const FunnelsTab = () => (
    <div className="row">
      {funnels.map((funnel, index) => (
        <div key={index} className="col-md-6 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="fas fa-filter me-2"></i>{funnel.name}
              </h6>
            </div>
            <div className="card-body">
              {funnel.steps.map((step, stepIndex) => (
                <div key={stepIndex} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <div className="fw-bold">{step.name}</div>
                      <div className="progress mt-1" style={{ height: '6px' }}>
                        <div 
                          className="progress-bar" 
                          style={{ 
                            width: `${step.conversionRate * 100}%`,
                            backgroundColor: step.conversionRate > 0.7 ? '#28a745' : 
                                           step.conversionRate > 0.5 ? '#ffc107' : '#dc3545'
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-end ms-3">
                      <div className="fw-bold">{step.users.toLocaleString()}</div>
                      <small className="text-muted">{Math.round(step.conversionRate * 100)}%</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const ABTestsTab = () => (
    <div className="row">
      {abTests.map((test, index) => (
        <div key={index} className="col-md-6 mb-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">{test.name}</h6>
              <span className={`badge ${test.status === 'Running' ? 'bg-success' : 'bg-secondary'}`}>
                {test.status}
              </span>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <div className="row">
                  <div className="col-6">
                    <div className="text-center">
                      <div className="h5 text-info">{test.lift}</div>
                      <small className="text-muted">Best Lift</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center">
                      <div className="h5 text-success">{Math.round(test.significance * 100)}%</div>
                      <small className="text-muted">Confidence</small>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <strong>Variants:</strong>
                {test.variants.map((variant, idx) => (
                  <div key={idx} className="d-flex justify-content-between mt-2 p-2 border rounded">
                    <div>
                      <div className="fw-bold">{variant.name}</div>
                      <small className="text-muted">{variant.traffic}% traffic</small>
                    </div>
                    <div className="text-end">
                      <div>{variant.conversions}/{variant.visitors}</div>
                      <small className="text-muted">{Math.round((variant.conversions / variant.visitors) * 100)}%</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">User Engagement Analytics</h2>
              <p className="text-muted">Industry-standard user behavior and engagement insights</p>
            </div>
            <button 
              className="btn btn-outline-primary"
              onClick={handleRefreshData}
              disabled={loading}
            >
              <i className="fas fa-sync-alt me-2"></i>Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-pills">
            {[
              { id: 'overview', label: 'Overview', icon: 'fas fa-tachometer-alt' },
              { id: 'behavioral', label: 'Behavioral', icon: 'fas fa-chart-area' },
              { id: 'journeys', label: 'User Journeys', icon: 'fas fa-route' },
              { id: 'cohort', label: 'Cohort Analysis', icon: 'fas fa-layer-group' },
              { id: 'funnels', label: 'Conversion Funnels', icon: 'fas fa-filter' },
              { id: 'abtests', label: 'A/B Tests', icon: 'fas fa-flask' }
            ].map(tab => (
              <li key={tab.id} className="nav-item">
                <button
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <i className={`${tab.icon} me-2`}></i>{tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'behavioral' && <BehavioralTab />}
        {activeTab === 'journeys' && <JourneysTab />}
        {activeTab === 'cohort' && <CohortTab />}
        {activeTab === 'funnels' && <FunnelsTab />}
        {activeTab === 'abtests' && <ABTestsTab />}
      </div>
    </div>
  );
};

export default UserEngagement;